import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { BaseTool, type IToolComponentProps, type IToolContext, type IToolManifest } from '../../core/tools/interfaces/ITool';
import type { ICommand } from '../../core/commands/ICommand';
import type { Node } from '../../core/models/scene';
import { useDiagramStore } from '../../stores/diagramStore';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { UpdateImageNodeAttrsCommand, type ImageNodeUpdate } from '../../core/commands/UpdateImageNodeAttrsCommand';
import { Tracker } from '../../domains/analytics/services/Tracker';
import { MEDIA } from '../../domains/analytics/constants/MetricEvents';
import { applyCropPanDelta, clampNormalizedCropRect, type NormalizedCropRect } from '../../domains/diagram/utils/imageShapeLayout';
import { resolveMediaSrc } from '../../domains/media/utils/resolveMediaSrc';
import { DiagramDocumentContext } from '../../contexts/DiagramDocumentContext';
import { useTranslation } from '../../hooks/useTranslation';

type ImageToolExecuteParams = {
  attrsPatch?: Record<string, unknown>;
  topLevelPatch?: Partial<Node>;
  source?: 'properties' | 'crop';
};

const getImageAttr = <T,>(node: Node | undefined, key: string, fallback: T): T => {
  if (!node) return fallback;
  const attrs = (node.attrs as Record<string, unknown> | undefined) ?? {};
  const value = attrs[key];
  if (typeof value === 'undefined' || value === null) return fallback;
  return value as T;
};

const CropEditorDialog: React.FC<{
  open: boolean;
  src: string;
  initialCrop: NormalizedCropRect | null;
  onOpenChange: (next: boolean) => void;
  onApply: (crop: NormalizedCropRect | null, sourceAspectRatio: number) => void;
}> = ({ open, src, initialCrop, onOpenChange, onApply }) => {
  const { t } = useTranslation('diagram');
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [containerElement, setContainerElement] = useState<HTMLDivElement | null>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const [draftCrop, setDraftCrop] = useState<NormalizedCropRect | null>(initialCrop);
  const [naturalSize, setNaturalSize] = useState({ width: 1, height: 1 });
  const [containerSize, setContainerSize] = useState({ width: 1, height: 1 });
  const dragStateRef = useRef<{
    mode: 'draw' | 'move';
    startX: number;
    startY: number;
    originCrop: NormalizedCropRect | null;
  } | null>(null);
  const wasOpenRef = useRef(false);

  useEffect(() => {
    if (open && !wasOpenRef.current) {
      setDraftCrop(initialCrop);
    }
    wasOpenRef.current = open;
  }, [open, initialCrop]);

  useEffect(() => {
    if (!open) {
      dragStateRef.current = null;
      return;
    }

    const container = containerElement;
    if (!container) return;

    let frameId = 0;
    const updateSize = () => {
      const nextWidth = Math.max(1, Math.round(container.clientWidth));
      const nextHeight = Math.max(1, Math.round(container.clientHeight));
      setContainerSize((prev) => {
        if (prev.width === nextWidth && prev.height === nextHeight) {
          return prev;
        }
        return { width: nextWidth, height: nextHeight };
      });
    };

    frameId = window.requestAnimationFrame(updateSize);
    const observer = new ResizeObserver(updateSize);
    observer.observe(container);

    return () => {
      if (frameId) {
        window.cancelAnimationFrame(frameId);
      }
      observer.disconnect();
    };
  }, [containerElement, open]);

  const renderedRect = useMemo(() => {
    const cw = containerSize.width || 1;
    const ch = containerSize.height || 1;
    const iw = naturalSize.width || 1;
    const ih = naturalSize.height || 1;
    const scale = Math.min(cw / iw, ch / ih);
    const width = iw * scale;
    const height = ih * scale;
    const x = (cw - width) / 2;
    const y = (ch - height) / 2;
    return { x, y, width, height };
  }, [containerSize.height, containerSize.width, naturalSize.height, naturalSize.width]);
  const sourceAspectRatio = useMemo(() => {
    const w = naturalSize.width || 1;
    const h = naturalSize.height || 1;
    return w / h;
  }, [naturalSize.height, naturalSize.width]);

  const screenCrop = useMemo(() => {
    if (!draftCrop || !renderedRect) return null;
    return {
      left: renderedRect.x + draftCrop.x * renderedRect.width,
      top: renderedRect.y + draftCrop.y * renderedRect.height,
      width: draftCrop.width * renderedRect.width,
      height: draftCrop.height * renderedRect.height,
    };
  }, [draftCrop, renderedRect]);

  const pointToLocal = (clientX: number, clientY: number) => {
    const container = containerRef.current;
    const rect = renderedRect;
    if (!container || !rect) return null;
    const bounds = container.getBoundingClientRect();
    const x = Math.max(rect.x, Math.min(rect.x + rect.width, clientX - bounds.left));
    const y = Math.max(rect.y, Math.min(rect.y + rect.height, clientY - bounds.top));
    return { x, y };
  };

  const toNormalized = (x: number, y: number) => {
    if (!renderedRect) return null;
    return {
      x: (x - renderedRect.x) / renderedRect.width,
      y: (y - renderedRect.y) / renderedRect.height,
    };
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{t('imageProperties.cropDialog.title')}</DialogTitle>
          <DialogDescription>{t('imageProperties.cropDialog.description')}</DialogDescription>
        </DialogHeader>
        <div
          ref={(el) => {
            containerRef.current = el;
            setContainerElement(el);
          }}
          data-testid="tid.image.crop.canvas"
          className="relative h-80 w-full overflow-hidden rounded-md border bg-muted"
          onMouseDown={(e) => {
            if (!renderedRect) return;
            if (renderedRect.width < 2 || renderedRect.height < 2) return;
            const point = pointToLocal(e.clientX, e.clientY);
            if (!point) return;
            const normalized = toNormalized(point.x, point.y);
            if (!normalized) return;
            if (
              draftCrop &&
              normalized.x >= draftCrop.x &&
              normalized.x <= draftCrop.x + draftCrop.width &&
              normalized.y >= draftCrop.y &&
              normalized.y <= draftCrop.y + draftCrop.height
            ) {
              dragStateRef.current = {
                mode: 'move',
                startX: point.x,
                startY: point.y,
                originCrop: draftCrop,
              };
              return;
            }
            dragStateRef.current = {
              mode: 'draw',
              startX: point.x,
              startY: point.y,
              originCrop: null,
            };
            setDraftCrop(clampNormalizedCropRect({ x: normalized.x, y: normalized.y, width: 0.01, height: 0.01 }));
          }}
          onMouseMove={(e) => {
            const state = dragStateRef.current;
            if (!state || !renderedRect) return;
            if (renderedRect.width < 2 || renderedRect.height < 2) return;
            const point = pointToLocal(e.clientX, e.clientY);
            if (!point) return;
            if (state.mode === 'move' && state.originCrop) {
              const deltaX = (point.x - state.startX) / renderedRect.width;
              const deltaY = (point.y - state.startY) / renderedRect.height;
              setDraftCrop(applyCropPanDelta(state.originCrop, deltaX, deltaY));
              return;
            }
            const x1 = Math.min(state.startX, point.x);
            const y1 = Math.min(state.startY, point.y);
            const x2 = Math.max(state.startX, point.x);
            const y2 = Math.max(state.startY, point.y);
            const n1 = toNormalized(x1, y1);
            const n2 = toNormalized(x2, y2);
            if (!n1 || !n2) return;
            setDraftCrop(
              clampNormalizedCropRect({
                x: n1.x,
                y: n1.y,
                width: n2.x - n1.x,
                height: n2.y - n1.y,
              }),
            );
          }}
          onMouseUp={() => {
            dragStateRef.current = null;
          }}
          onMouseLeave={() => {
            dragStateRef.current = null;
          }}
        >
          <img
            ref={imageRef}
            src={src}
            alt={t('imageProperties.cropDialog.previewAlt')}
            className="h-full w-full object-contain select-none"
            draggable={false}
            onLoad={() => {
              const image = imageRef.current;
              setNaturalSize({
                width: image?.naturalWidth || 1,
                height: image?.naturalHeight || 1,
              });
            }}
          />
          {screenCrop ? (
            <>
              <div className="absolute inset-0 bg-black/35 pointer-events-none" />
              <div
                className="absolute border-2 border-white bg-transparent shadow-[0_0_0_9999px_rgba(0,0,0,0.35)] pointer-events-none"
                style={{
                  left: `${screenCrop.left}px`,
                  top: `${screenCrop.top}px`,
                  width: `${screenCrop.width}px`,
                  height: `${screenCrop.height}px`,
                }}
              />
            </>
          ) : null}
        </div>
        <DialogFooter>
          <div className="flex w-full items-center justify-between gap-2">
            <Button
              type="button"
              variant="outline"
              data-testid="tid.image.crop.reset"
              onClick={() => setDraftCrop(null)}
            >
              {t('imageProperties.cropDialog.reset')}
            </Button>
            <div className="flex items-center gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                {t('imageProperties.cropDialog.cancel')}
              </Button>
              <Button
                type="button"
                data-testid="tid.image.crop.apply"
                onClick={() => {
                  onApply(draftCrop, sourceAspectRatio);
                  onOpenChange(false);
                }}
              >
                {t('imageProperties.cropDialog.apply')}
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ImagePropertiesPanel: React.FC<IToolComponentProps> = ({ context, onExecute }) => {
  const { t } = useTranslation('diagram');
  const diagramDoc = useContext(DiagramDocumentContext);
  const selectedIds = useMemo(() => Array.from(context.selection), [context.selection, context.selectionSummary?.version]);
  const nodes = useDiagramStore((s) => s.nodes);
  const selectedImages = useMemo(
    () =>
      nodes.filter(
        (node) => selectedIds.includes(node.id) && node.type === 'image',
      ),
    [nodes, selectedIds],
  );

  if (selectedImages.length === 0) {
    return (
      <div className="px-4 py-3 text-xs text-muted-foreground">
        {t('imageProperties.selectPrompt')}
      </div>
    );
  }

  return (
    <ImagePropertiesPanelContent
      selectedImages={selectedImages}
      diagramId={diagramDoc?.diagramId}
      onExecute={onExecute}
      t={t}
    />
  );
};

const ImagePropertiesPanelContent: React.FC<{
  selectedImages: Node[];
  diagramId?: string;
  onExecute: IToolComponentProps['onExecute'];
  t: ReturnType<typeof useTranslation>['t'];
}> = ({ selectedImages, diagramId, onExecute, t }) => {
  const primary = selectedImages[0];
  const opacity = Number(getImageAttr<number>(primary, 'opacity', 100));
  const borderEnabled = Boolean(getImageAttr<boolean>(primary, 'borderEnabled', true));
  const borderWidth = Number(getImageAttr<number>(primary, 'borderWidth', 1));
  const borderColor = String(getImageAttr<string>(primary, 'borderColor', '#CBD5E1'));
  const fit = String(getImageAttr<string>(primary, 'fit', 'cover')) === 'contain' ? 'contain' : 'cover';
  const alt = String(getImageAttr<string>(primary, 'alt', ''));
  const caption = String(getImageAttr<string>(primary, 'caption', ''));
  const aspectRatioLocked = Boolean(getImageAttr<boolean>(primary, 'aspectRatioLocked', false));
  const crop = clampNormalizedCropRect((getImageAttr<Record<string, unknown> | null>(primary, 'crop', null)));
  const [cropDialogOpen, setCropDialogOpen] = useState(false);
  const objectKey = String(getImageAttr<string>(primary, 'objectKey', getImageAttr<string>(primary, 'photoRef', '')));
  const cropPreviewSrc = resolveMediaSrc(objectKey, { diagramId });
  const currentWidth = Number(primary.width ?? primary.w ?? 240);
  const currentHeight = Number(primary.height ?? primary.h ?? 160);
  const [sourceAspectRatio, setSourceAspectRatio] = useState(() =>
    Math.max(1, currentWidth) / Math.max(1, currentHeight),
  );

  useEffect(() => {
    if (!cropPreviewSrc) return;
    const image = new window.Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => {
      const w = image.naturalWidth || 1;
      const h = image.naturalHeight || 1;
      setSourceAspectRatio(w / h);
    };
    image.src = cropPreviewSrc;
  }, [cropPreviewSrc]);

  return (
    <div className="px-4 py-3 space-y-4">
      <div>
        <h3 className="text-sm font-semibold text-foreground">{t('imageProperties.panelTitle')}</h3>
        <p className="text-xs text-muted-foreground">
          {selectedImages.length > 1
            ? t('imageProperties.subtitleMulti', { count: selectedImages.length })
            : t('imageProperties.subtitleSingle')}
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="image-fit-select">{t('imageProperties.fit')}</Label>
        <Select
          value={fit}
          onValueChange={(value) => onExecute({ attrsPatch: { fit: value }, source: 'properties' })}
          disabled={!!crop}
        >
          <SelectTrigger id="image-fit-select" data-testid="tid.image.properties.fit">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="cover">{t('imageProperties.fitCover')}</SelectItem>
            <SelectItem value="contain">{t('imageProperties.fitContain')}</SelectItem>
          </SelectContent>
        </Select>
        {crop ? (
          <p className="text-xs text-muted-foreground">
            {t('imageProperties.fitDisabledHint')}
          </p>
        ) : null}
      </div>

      <div className="space-y-2">
        <Label htmlFor="image-opacity-range">{t('imageProperties.opacityLabel', { percent: opacity })}</Label>
        <Input
          id="image-opacity-range"
          data-testid="tid.image.properties.opacity"
          type="range"
          className="h-2 px-0 py-0 border-0 bg-transparent hover:border-0 hover:bg-transparent focus-visible:ring-0 focus-visible:border-0"
          min={0}
          max={100}
          step={1}
          value={Math.max(0, Math.min(100, opacity))}
          onChange={(e) =>
            onExecute({
              attrsPatch: { opacity: Number(e.target.value) },
              source: 'properties',
            })
          }
        />
      </div>

      <div className="space-y-3 rounded-md border px-3 py-3">
        <div className="flex items-center justify-between">
          <Label htmlFor="image-border-enabled" className="text-sm">{t('imageProperties.border')}</Label>
          <Switch
            id="image-border-enabled"
            data-testid="tid.image.properties.border-enabled"
            checked={borderEnabled}
            onCheckedChange={(checked) => onExecute({ attrsPatch: { borderEnabled: checked }, source: 'properties' })}
          />
        </div>
        <div className="grid grid-cols-[1fr_auto] gap-2">
          <Input
            data-testid="tid.image.properties.border-color"
            type="color"
            value={borderColor}
            disabled={!borderEnabled}
            onChange={(e) => onExecute({ attrsPatch: { borderColor: e.target.value }, source: 'properties' })}
          />
          <Input
            data-testid="tid.image.properties.border-width"
            type="number"
            min={0}
            max={12}
            step={1}
            className="w-20"
            value={Number.isFinite(borderWidth) ? borderWidth : 1}
            disabled={!borderEnabled}
            onChange={(e) =>
              onExecute({
                attrsPatch: { borderWidth: Math.max(0, Math.min(12, Number(e.target.value) || 0)) },
                source: 'properties',
              })
            }
          />
        </div>
      </div>

      <div className="flex items-center justify-between rounded-md border px-3 py-2">
        <Label htmlFor="image-aspect-lock" className="text-sm">{t('imageProperties.lockAspectRatio')}</Label>
        <Switch
          id="image-aspect-lock"
          data-testid="tid.image.properties.aspect-lock"
          checked={aspectRatioLocked}
          onCheckedChange={(checked) => onExecute({ attrsPatch: { aspectRatioLocked: checked }, source: 'properties' })}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="image-alt-input">{t('imageProperties.altText')}</Label>
        <Input
          id="image-alt-input"
          data-testid="tid.image.properties.alt"
          value={alt}
          onChange={(e) => onExecute({ attrsPatch: { alt: e.target.value }, source: 'properties' })}
          placeholder={t('imageProperties.altPlaceholder')}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="image-caption-input">{t('imageProperties.caption')}</Label>
        <Textarea
          id="image-caption-input"
          data-testid="tid.image.properties.caption"
          value={caption}
          onChange={(e) => onExecute({ attrsPatch: { caption: e.target.value }, source: 'properties' })}
          rows={2}
          placeholder={t('imageProperties.captionPlaceholder')}
        />
      </div>

      <div className="space-y-2 rounded-md border px-3 py-3">
        <div className="text-sm font-medium">{t('imageProperties.crop')}</div>
        <p className="text-xs text-muted-foreground break-words min-w-0">
          {crop
            ? t('imageProperties.cropApplied', {
                w: Math.round(crop.width * 100),
                h: Math.round(crop.height * 100),
              })
            : t('imageProperties.noCrop')}
        </p>
        <Button
          type="button"
          variant="outline"
          className="w-full"
          data-testid="tid.image.crop.open"
          onClick={() => setCropDialogOpen(true)}
        >
          {t('imageProperties.openCropEditor')}
        </Button>
        {crop ? (
          <Button
            type="button"
            variant="ghost"
            className="h-8 px-2 text-xs"
            onClick={() => {
              const width = Math.max(20, currentWidth || 240);
              const nextHeight = Math.max(20, width / Math.max(0.01, sourceAspectRatio));
              onExecute({
                attrsPatch: { crop: null },
                topLevelPatch: { width, w: width, height: nextHeight, h: nextHeight },
                source: 'crop',
              });
            }}
          >
            {t('imageProperties.clearCrop')}
          </Button>
        ) : null}
      </div>
      <CropEditorDialog
        open={cropDialogOpen}
        src={cropPreviewSrc}
        initialCrop={crop}
        onOpenChange={setCropDialogOpen}
        onApply={(nextCrop, dialogAspectRatio) => {
          const width = Math.max(20, currentWidth || 240);
          const effectiveAspect = nextCrop
            ? Math.max(0.01, dialogAspectRatio * (nextCrop.width / nextCrop.height))
            : Math.max(0.01, dialogAspectRatio);
          const nextHeight = Math.max(20, width / effectiveAspect);
          onExecute({
            attrsPatch: { crop: nextCrop ?? null },
            topLevelPatch: { width, w: width, height: nextHeight, h: nextHeight },
            source: 'crop',
          });
        }}
      />
    </div>
  );
};

export class ImagePropertiesTool extends BaseTool {
  manifest: IToolManifest = {
    id: 'image-properties',
    name: 'imageProperties.toolName',
    icon: 'image',
    category: 'style',
    description: 'Adjust image appearance and metadata',
    version: '1.0.0',
    requiresSelection: true,
    minSelection: 1,
    ui: {
      toolbar: false,
      contextMenu: false,
      propertyPanel: true,
      sidebar: false,
      floatingPanel: false,
      statusBar: false,
    },
    applicability: {
      contextTypes: ['image'],
    },
    capabilities: {
      modifiesShapes: true,
      createsShapes: false,
      deletesShapes: false,
      modifiesConnectors: false,
      createsConnectors: false,
      deletesConnectors: false,
      requiresUserInput: true,
      supportsMultiSelect: true,
      supportsBatchOperations: true,
    },
  };

  private getSelectedImageIds(context: IToolContext): string[] {
    const selected = Array.from(context.selection);
    if (selected.length === 0) return [];
    const store = useDiagramStore.getState();
    return selected.filter((id) => {
      const node = store.nodes.find((n) => n.id === id && n.type === 'image');
      return Boolean(node);
    });
  }

  canExecute(context: IToolContext): boolean {
    if (!super.canExecute(context)) return false;
    return this.getSelectedImageIds(context).length > 0;
  }

  execute(params?: ImageToolExecuteParams): ICommand | null {
    if (!this.context) return null;

    const nodeIds = this.getSelectedImageIds(this.context);
    if (nodeIds.length === 0) return null;

    const updates: ImageNodeUpdate[] = nodeIds.map((nodeId) => {
      const attrsPatch = params?.attrsPatch ?? {};
      const topLevelPatch = params?.topLevelPatch ?? {};
      return {
        nodeId,
        changes: {
          ...topLevelPatch,
          attrs: attrsPatch,
        },
      };
    });

    if (params?.source === 'properties') {
      Tracker.track(MEDIA.CANVAS_IMAGE_PROPERTIES_EDITED);
    }
    if (params?.source === 'crop') {
      Tracker.track(MEDIA.CANVAS_IMAGE_CROPPED);
    }

    return new UpdateImageNodeAttrsCommand(updates);
  }

  getPropertyPanelComponent() {
    return ImagePropertiesPanel;
  }
}
